import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../api/apiService';
import './TablePages.css';

interface DistributorRow {
  id: number;
  distributor_agency_name: string;
  distributor_code: string;
  owner_name: string;
  owner_mobile: string;
  status: string;
  payment_status: string;
  registration_fee_amount: number;
  created_at: string;
}

interface DriverRow {
  id: number;
  driver_name: string;
  mobile_number: string;
  vehicle_number: string;
  vehicle_type: string;
  is_available: boolean;
  is_blocked: boolean;
  distributor_id: number;
  distributor_name: string;
  created_at: string;
}

const Distributors: React.FC = () => {
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status') || '';
  const [list, setList] = useState<DistributorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>(statusFromUrl);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [driversModal, setDriversModal] = useState<{ distributorName: string; distributorId: number } | null>(null);
  const [driversList, setDriversList] = useState<DriverRow[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);

  useEffect(() => {
    setStatusFilter(statusFromUrl);
  }, [statusFromUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/admin/distributors?status=${encodeURIComponent(statusFilter)}` : '/admin/distributors';
      const res = await apiService.get(url);
      // Backend returns a JSON array; axios puts it in res.data
      setList(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load distributors', e);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id: number) => {
    try {
      await apiService.put(`/admin/distributors/${id}/approve`);
      load();
      setDetail(null);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const handleReject = async (id: number) => {
    try {
      await apiService.put(`/admin/distributors/${id}/reject`);
      load();
      setDetail(null);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const handleBlock = async (id: number) => {
    try {
      await apiService.put(`/admin/distributors/${id}/block`);
      load();
      setDetail(null);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const handleMarkRegistrationPaid = async (id: number) => {
    try {
      await apiService.put(`/admin/distributors/${id}/mark-registration-paid`);
      load();
      setDetail(null);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const showDetail = async (id: number) => {
    try {
      const { data } = await apiService.get(`/admin/distributors/${id}`);
      setDetail(data);
    } catch (e) {
      console.error(e);
    }
  };

  const showDrivers = async (distributorId: number, distributorName: string) => {
    setDriversModal({ distributorName, distributorId });
    setDriversList([]);
    setDriversLoading(true);
    try {
      const { data } = await apiService.get(`/admin/drivers?distributor_id=${distributorId}`);
      setDriversList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setDriversLoading(false);
    }
  };

  const handleDriverBlock = async (driverId: number) => {
    try {
      await apiService.put(`/admin/drivers/${driverId}/block`);
      if (driversModal) showDrivers(driversModal.distributorId, driversModal.distributorName);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDriverUnblock = async (driverId: number) => {
    try {
      await apiService.put(`/admin/drivers/${driverId}/unblock`);
      if (driversModal) showDrivers(driversModal.distributorId, driversModal.distributorName);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const formatDate = (s: string) => (s ? new Date(s).toLocaleString() : '-');

  return (
    <div className="table-page">
      <div className="table-toolbar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="table-select"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="pending_approval">Pending approval (new registrations)</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
        <div className="table-toolbar-right">
          <button type="button" onClick={load} className="btn-primary">Refresh</button>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>Code</th>
                <th>Owner</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Fee (₹)</th>
                <th>Payment</th>
                <th>Drivers</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={10}>No distributors yet.</td>
                </tr>
              ) : (
                list.map((row) => (
                  <tr key={row.id}>
                    <td>{row.distributor_agency_name}</td>
                    <td>{row.distributor_code}</td>
                    <td>{row.owner_name}</td>
                    <td>{row.owner_mobile}</td>
                    <td><span className={`badge badge-${row.status}`}>{row.status}</span></td>
                    <td>{row.registration_fee_amount}</td>
                    <td>
                      <div className="table-cell-row">
                        <span className={`badge badge-${row.payment_status === 'paid' ? 'approved' : 'pending_approval'}`}>
                          {row.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                        {row.payment_status !== 'paid' && Number(row.registration_fee_amount) > 0 && (
                          <button type="button" className="btn-sm green" onClick={() => handleMarkRegistrationPaid(row.id)} title="Mark registration fee as paid">
                            Mark paid
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <button type="button" className="btn-sm" onClick={() => showDrivers(row.id, row.distributor_agency_name)}>
                        View drivers
                      </button>
                    </td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="btn-sm" onClick={() => showDetail(row.id)}>View</button>
                        {row.status === 'pending_approval' && (
                          <>
                            <button type="button" className="btn-sm green" onClick={() => handleApprove(row.id)}>Approve</button>
                            <button type="button" className="btn-sm red" onClick={() => handleReject(row.id)}>Reject</button>
                          </>
                        )}
                        {row.status === 'approved' && (
                          <button type="button" className="btn-sm red" onClick={() => handleBlock(row.id)}>Block</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Distributor details</h3>
            <pre className="modal-pre">{JSON.stringify(detail, null, 2)}</pre>
            <button type="button" className="btn-primary" onClick={() => setDetail(null)}>Close</button>
          </div>
        </div>
      )}
      {driversModal && (
        <div className="modal-overlay" onClick={() => setDriversModal(null)}>
          <div className="modal-card modal-card-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Drivers — {driversModal.distributorName}</h3>
            {driversLoading ? (
              <p>Loading drivers...</p>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Vehicle</th>
                      <th>Type</th>
                      <th>Available</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driversList.length === 0 ? (
                      <tr>
                        <td colSpan={7}>No drivers for this distributor.</td>
                      </tr>
                    ) : (
                      driversList.map((d) => (
                        <tr key={d.id}>
                          <td>{d.driver_name}</td>
                          <td>{d.mobile_number}</td>
                          <td>{d.vehicle_number}</td>
                          <td>{d.vehicle_type}</td>
                          <td>{d.is_available ? 'Yes' : 'No'}</td>
                          <td>
                            <span className={d.is_blocked ? 'badge badge-suspended' : 'badge badge-approved'}>
                              {d.is_blocked ? 'Blocked' : 'Active'}
                            </span>
                          </td>
                          <td>
                            {d.is_blocked ? (
                              <button type="button" className="btn-sm green" onClick={() => handleDriverUnblock(d.id)}>Unblock</button>
                            ) : (
                              <button type="button" className="btn-sm red" onClick={() => handleDriverBlock(d.id)}>Block</button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setDriversModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Distributors;
